import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import apiClient from '@/api-client';
import { logger } from '@/lib/default-logger';
import { OAuthFlowError, type OAuthCompleteInput, type OAuthStart } from '@/lib/oauth/flow';
import { toast } from '@/components/core/toaster';

export interface OAuthStatus {
  /** False when the platform OAuth client is not set up: connecting is impossible. */
  configured: boolean;
  status: 'connected' | 'disconnected' | 'reconnect_required';
  /** Provider-side account label. */
  caption: string | null;
  updatedAt: string | null;
}

export const oauthKeys = {
  all: ['oauth'] as const,
  status: (pluginId: string) => [...oauthKeys.all, 'status', pluginId] as const,
};

export function oauthErrorMessage(error: any): string {
  if (error instanceof OAuthFlowError) return error.message;
  const data = error?.response?.data;
  // Expired, reused, or started in another session.
  if (data?.code === 'oauth_state_invalid') return 'Connection request expired, try again';
  return (Array.isArray(data?.message) ? data.message[0] : data?.message) || 'Something went wrong!';
}

export function useOAuthStatus(pluginId: string) {
  return useQuery({
    queryKey: oauthKeys.status(pluginId),
    queryFn: async (): Promise<OAuthStatus> => {
      const response = await apiClient.get(`/oauth/${pluginId}`);
      return response.data;
    },
    enabled: !!pluginId,
  });
}

// Connecting shows its own errors: a toast in the plugin drawer, inline on the callback page.
// So `useStartOAuth` and `useCompleteOAuth` only log them.

export function useStartOAuth() {
  return useMutation({
    mutationFn: async ({ pluginId, options }: { pluginId: string; options?: Record<string, unknown> }): Promise<OAuthStart> => {
      const response = await apiClient.post('/oauth/start', { pluginId, options });
      return response.data;
    },
    onError: (error: any) => {
      logger.error(error);
    },
  });
}

export function useCompleteOAuth() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: OAuthCompleteInput): Promise<OAuthStatus> => {
      const response = await apiClient.post('/oauth/complete', input);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: oauthKeys.all });
      toast.success('Account connected');
    },
    onError: (error: any) => {
      logger.error(error);
    },
  });
}

export function useDisconnectOAuth() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (pluginId: string): Promise<void> => {
      await apiClient.delete(`/oauth/${pluginId}`);
    },
    onSuccess: (_, pluginId) => {
      queryClient.invalidateQueries({ queryKey: oauthKeys.status(pluginId) });
      toast.success('Account disconnected');
    },
    onError: (error: any) => {
      logger.error(error);
      toast.error(oauthErrorMessage(error));
    },
  });
}
